import { useEffect, useState } from 'react';
import { AppState, Platform } from 'react-native';

/**
 * Tracks whether the app is in a state where it should be doing foreground
 * work (HTTP polling, token streaming, etc.).
 *
 *  - Web: `true` when `document.visibilityState === 'visible'`. Going
 *    `hidden` (background tab, minimized window) returns `false`, which
 *    lets pollers short-circuit and reduce backend load.
 *  - Native: `true` when `AppState.currentState === 'active'`. Going
 *    `inactive`/`background` (app switcher, control center, lock screen)
 *    returns `false`. On native the JS thread is usually suspended in the
 *    background anyway, but `inactive` (notification center, control center
 *    without full background) is *not* suspended — so this gate still
 *    matters there.
 *
 * Combine with `useIsFocused()` from `@react-navigation/native` if the
 * component is on a tabbed/stacked route and should only run when it is
 * itself the focused route. See `components/Messages/MessageInferenceJob.jsx`
 * for the canonical usage of both gates together.
 *
 * @returns {boolean} true when foreground work is appropriate
 */
export default function useAppActive() {
  const [active, setActive] = useState(() => {
    if (Platform.OS === 'web') {
      return typeof document === 'undefined'
        ? true
        : document.visibilityState === 'visible';
    }
    return AppState.currentState === 'active';
  });

  useEffect(() => {
    if (Platform.OS === 'web') {
      if (typeof document === 'undefined') return undefined;
      const handler = () =>
        setActive(document.visibilityState === 'visible');
      document.addEventListener('visibilitychange', handler);
      return () => document.removeEventListener('visibilitychange', handler);
    }
    const sub = AppState.addEventListener('change', (state) =>
      setActive(state === 'active'),
    );
    return () => sub?.remove?.();
  }, []);

  return active;
}
